/**
 * Renders non-reversal line items that are not listed in the
 * `LINE_ITEMS` array in util/types.js
 *
 * The line items are rendered so that the line item code is formatted to human
 * readable form and the line total is printed as price.
 *
 * If you require another kind of presentation for your line items, add them to
 * the `LINE_ITEMS` array in util/types.js and create a specific line item
 * component for them that can be used in the `OrderBreakdown` component.
 */
import React from 'react';
import { arrayOf, shape, string } from 'prop-types';
import { intlShape } from '../../util/reactIntl';
import { convertMoneyToNumber, formatAmountOnly } from '../../util/currency';
import { CurrencySymbol } from '../../components';
import { humanizeLineItemCode } from '../../util/data';
import {
  LINE_ITEMS,
  LINE_ITEM_VAT_TAX,
  LINE_ITEM_DEAL_DISCOUNT,
  LINE_ITEM_TRAVEL,
  propTypes,
} from '../../util/types';

import css from './OrderBreakdown.module.css';

// Matches the `line-item/material-item-N` codes buildListingLevelExtraLineItems
// (server/api-util/lineItems.js + cartLineItems.js) generates from a Fixed/Hourly
// listing's declared materials — N (1-based) indexes into materialsSnapshot,
// the point-in-time copy of those materials' names saved on the transaction at
// booking time (see server/api/initiate-privileged.js).
const MATERIAL_ITEM_CODE = /^line-item\/material-item-(\d+)$/;

const LineItemUnknownItemsMaybe = props => {
  const { lineItems, isProvider, intl, materialsSnapshot } = props;

  // resolve unknown non-reversal line items
  const allItems = lineItems.filter(item => LINE_ITEMS.indexOf(item.code) === -1 && !item.reversal);

  const items = isProvider
    ? allItems.filter(item => item.includeFor.includes('provider'))
    : allItems.filter(item => item.includeFor.includes('customer'));

  return items.length > 0 ? (
    <React.Fragment>
      {items.map((item, i) => {
        const { quantity, code, percentage } = item || {};
        const isVat = code === LINE_ITEM_VAT_TAX;
        const isDealDiscount = code === LINE_ITEM_DEAL_DISCOUNT;
        const isTravel = code === LINE_ITEM_TRAVEL;
        const materialItemMatch = code.match(MATERIAL_ITEM_CODE);
        // Falls back to the generic humanized code when there's no snapshot
        // (transactions created before this feature) or the index is out of
        // range for it.
        const materialName = materialItemMatch
          ? materialsSnapshot?.[Number(materialItemMatch[1]) - 1]?.name
          : null;

        const label = isVat
          ? intl.formatMessage({ id: 'lineItem.vat-tax-label' })
          : isTravel
          ? intl.formatMessage({ id: 'lineItem.travel-label' })
          : isDealDiscount
          ? Number.isFinite(percentage)
            ? intl.formatMessage(
                { id: 'lineItem.deal-discount-percentage-label' },
                { percentage: Math.abs(percentage) }
              )
            : intl.formatMessage({ id: 'lineItem.deal-discount-label' })
          : materialName
          ? quantity && quantity > 1
            ? `${materialName} x ${quantity}`
            : materialName
          : materialItemMatch
          ? intl.formatMessage({ id: 'OrderBreakdown.materialFallbackLabel' })
          : quantity && quantity > 1
          ? `${humanizeLineItemCode(item.code)} x ${quantity}`
          : humanizeLineItemCode(item.code);

        const totalAmount = convertMoneyToNumber(item.lineTotal);
        const formattedTotal = formatAmountOnly(intl, totalAmount, 2);
        return (
          <div key={`${i}-item.code`} className={css.lineItem}>
            <span className={css.itemLabel}>{label}</span>
            <span className={css.itemValue}>
              <CurrencySymbol size={16} />
              <span>&nbsp;{formattedTotal}</span>
            </span>
          </div>
        );
      })}
    </React.Fragment>
  ) : null;
};

LineItemUnknownItemsMaybe.propTypes = {
  lineItems: propTypes.lineItems.isRequired,
  materialsSnapshot: arrayOf(shape({ name: string })),
  intl: intlShape.isRequired,
};

export default LineItemUnknownItemsMaybe;
